import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { votacaoApi } from '@/lib/api/votacao';
import { pesquisasApi } from '@/lib/api/pesquisas';
import { candidatosApi } from '@/lib/api/candidatos';
import { useAuthStore } from '@/lib/stores/authStore';
import { useMockStore } from '@/lib/stores/mockStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { useEscala } from '@/lib/escala';
import { MapaBairros, type BairroVoto } from './MapaBairros';
import { HistoricoSecao } from './HistoricoSecao';
import { Info } from '@/design-system/Info';

/**
 * Visão geral eleitoral (rota `/eleg`, proto:740 — `RENDER.eleg`).
 *
 * `RENDER.eleg` é redefinido cinco vezes no protótipo (proto:740, 954, 1392,
 * 1400, 1420), cada uma envolvendo a anterior (`_elegOld`/`_elegPrev*`) — a
 * última vence. Esta primeira versão traz apenas o cabeçalho e o mapa
 * coroplético (proto:740-748); os blocos seguintes (rankings, chips dos
 * estrategistas, pesquisas recentes e `histSec()`) chegam nos próximos
 * commits desta mesma tarefa.
 *
 * O h1 é "Painel territorial" (proto:743) — o rótulo do menu ("Visão geral
 * eleitoral", proto:520) é o nome do destino, não a manchete da página; o
 * mesmo padrão documentado em `titulos.test.tsx`.
 */
export function VisaoGeralPage() {
  const token = useAuthStore((s) => s.token) || '';
  const navigate = useNavigate();
  const bairroActiveId = useMockStore((s) => s.bairroActiveId);
  const setBairroActiveId = useMockStore((s) => s.setBairroActiveId);
  const { unidade } = useEscala();
  const { bairros } = useBairros();

  const { data: candidatos = [] } = useQuery({
    queryKey: ['candidatos'],
    queryFn: () => candidatosApi.list(token),
    enabled: !!token,
  });

  const doMandato = candidatos[0];

  const ciclos = [...new Set(candidatos.flatMap((c) => c.candidaturas.map((cd) => cd.ciclo)))].sort(
    (a, b) => b - a,
  );
  const [ciclo, setCiclo] = useState<number | null>(null);
  const cicloAtivo = ciclo ?? ciclos[0] ?? 2024;

  const { data: desempenho } = useQuery({
    queryKey: ['votacao', cicloAtivo],
    queryFn: () => votacaoApi.getDesempenho(cicloAtivo, token),
    enabled: !!token,
  });

  const meuDesempenho = desempenho?.desempenho_geral.find((g) => g.nome === doMandato?.nome);
  const numeroAtivo = doMandato?.candidaturas.find((c) => c.ciclo === cicloAtivo)?.numero_urna;

  const { data: pesquisas = [] } = useQuery({
    queryKey: ['pesquisas'],
    queryFn: () => pesquisasApi.list(token),
    enabled: !!token,
  });
  const pesquisasRecentes = pesquisas.filter((p) => p.confianca !== 'baixa');

  const bairrosVoto: BairroVoto[] = bairros.map((b) => ({
    id: b.id,
    nome: b.n,
    votos: b.votos,
    percentual: b.pct,
    path: b.path,
    lx: b.lx,
    ly: b.ly,
  }));

  const abrirTerritorio = (b: BairroVoto) => {
    setBairroActiveId(b.id);
    navigate('/territorio');
  };

  const bairroAtivo = bairros.find((b) => b.id === bairroActiveId) || bairros[0];

  const ranking = [...bairrosVoto].sort((a, b) => b.votos - a.votos);
  const maisVotados = ranking.slice(0, 5);
  const menosVotados = [...ranking].reverse().slice(0, 5);
  const bairroPorId = new Map(bairros.map((b) => [b.id, b]));

  return (
    <div className="page" id="p-eleg">
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>Painel territorial</h1>
          <p>
            {/*
              proto:743 diz "agregados por zona, seção e bairro". Seção não
              entra: `eleg/tse/tse-import.ts` grava `secao: null` de propósito,
              porque o arquivo publicado pelo TSE já vem agregado por zona.
            */}
            Módulo Eleg: leitura do território com dados públicos do TSE (schema eleg, isolado por
            RLS). Sem classificação de pessoas: apenas agregados por zona eleitoral e{' '}
            {unidade.toLowerCase()}.
          </p>
        </div>
        <div className="act">
          {ciclos.length > 0 && (
            <select className="sel" value={cicloAtivo} onChange={(e) => setCiclo(Number(e.target.value))}>
              {ciclos.map((c) => (
                <option key={c} value={c}>
                  Eleição {c}
                </option>
              ))}
            </select>
          )}
          {doMandato && (
            <span className="sel">
              Candidatura: {doMandato.nome}
              {numeroAtivo ? ` (${numeroAtivo})` : ''}
            </span>
          )}
        </div>
      </div>

      <div className="grid g21">
        <div className="card">
          <h2>
            Votação por {unidade.toLowerCase()}
            <span className="r" style={{ color: 'var(--eleg)' }} onClick={() => navigate('/territorio')}>
              Abrir perfil de {bairroAtivo?.n ?? 'território'}
            </span>
          </h2>
          <MapaBairros bairros={bairrosVoto} aoClicar={abrirTerritorio} unidade={unidade} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card kpi">
            <div className="l">Votos {cicloAtivo}</div>
            <div className="v" style={{ color: 'var(--eleg)' }}>
              {(meuDesempenho?.votos ?? 0).toLocaleString('pt-BR')}
            </div>
            <div className="d">{meuDesempenho?.percentual ?? 0}% das zonas carregadas</div>
          </div>

          <div className="card">
            <h2>
              Ranking · mais votos
              <Link className="r" style={{ color: 'var(--eleg)' }} to="/controle-votacao">
                Metas
              </Link>
            </h2>
            <div className="rank">
              {maisVotados.map((b, i) => (
                <Link
                  key={b.id}
                  to="/controle-votacao"
                  onClick={() => setBairroActiveId(b.id)}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div>
                    <b style={{ width: 18, color: 'var(--ink3)' }}>{i + 1}</b>
                    <span style={{ width: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.nome}
                    </span>
                    <span className="b">
                      <i style={{ width: `${(b.votos / (maisVotados[0]?.votos || 1)) * 100}%`, background: 'var(--eleg)' }} />
                    </span>
                    <b className="num" style={{ width: 48, textAlign: 'right' }}>
                      {b.votos.toLocaleString('pt-BR')}
                    </b>
                    <span style={{ fontSize: 11, color: 'var(--ink3)', width: 44, textAlign: 'right' }}>
                      {b.percentual}%
                    </span>
                  </div>
                </Link>
              ))}
              {maisVotados.length === 0 && <div className="empty">Nenhum {unidade.toLowerCase()} cadastrado.</div>}
            </div>
          </div>

          <div className="card">
            <h2>
              Ranking · menos votos{' '}
              <Info titulo="Como ler o ranking de menos votos">
                Território fraco com necessidade alta é oportunidade de presença.
              </Info>
            </h2>
            <div className="rank">
              {menosVotados.map((b, i) => (
                <Link
                  key={b.id}
                  to="/controle-votacao"
                  onClick={() => setBairroActiveId(b.id)}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div>
                    <b style={{ width: 18, color: 'var(--ink3)' }}>{ranking.length - i}</b>
                    <span style={{ width: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.nome}
                    </span>
                    <span className="b">
                      <i style={{ width: `${(b.votos / (maisVotados[0]?.votos || 1)) * 100}%`, background: 'var(--warn)' }} />
                    </span>
                    <b className="num" style={{ width: 48, textAlign: 'right' }}>
                      {b.votos.toLocaleString('pt-BR')}
                    </b>
                    <span style={{ fontSize: 11, color: 'var(--ink3)', width: 44, textAlign: 'right' }}>
                      {b.percentual}%
                    </span>
                  </div>
                </Link>
              ))}
              {menosVotados.length === 0 && <div className="empty">Nenhum {unidade.toLowerCase()} cadastrado.</div>}
            </div>
            {menosVotados.length > 0 && (
              <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '8px 0 0' }}>
                Necessidade:{' '}
                {[...menosVotados]
                  .slice(0, 3)
                  .map((b) => `${b.nome} ${bairroPorId.get(b.id)?.nec ?? 0}`)
                  .join(' · ')}
              </p>
            )}
          </div>

          <div className="card" style={{ background: 'var(--eleg-soft)', borderColor: 'var(--eleg)' }}>
            <h3 style={{ color: 'var(--eleg)' }}>Estrategistas de IA</h3>
            <div className="filters" style={{ marginTop: 8 }}>
              {['O Cartógrafo', 'O Tático', 'O Sentinela', 'A Voz', 'O Maestro'].map((p) => (
                <span key={p} className="chip eleg chipbtn" onClick={() => navigate('/territorio')}>
                  {p}
                </span>
              ))}
            </div>
            <p style={{ fontSize: 12, margin: '8px 0 0' }}>
              Leituras geradas sobre agregados públicos.
            </p>
          </div>

          <div className="card">
            <h2>
              Pesquisas recentes
              <Link className="r" style={{ color: 'var(--eleg)' }} to="/pesquisas">
                Ver
              </Link>
            </h2>
            {pesquisasRecentes.length === 0 ? (
              <div className="empty">Nenhuma pesquisa cadastrada.</div>
            ) : (
              pesquisasRecentes.map((p) => {
                const resultado = doMandato ? p.resultados[doMandato.nome] : undefined;
                return (
                  <div
                    key={p.id}
                    className="n"
                    style={{
                      display: 'flex',
                      gap: 8,
                      padding: '6px 0',
                      borderBottom: '1px solid var(--line)',
                      fontSize: 13,
                    }}
                  >
                    <span>{p.instituto}</span>
                    <span style={{ color: 'var(--ink3)' }}>{p.periodo}</span>
                    <b className="num" style={{ marginLeft: 'auto' }}>
                      {resultado != null ? `${resultado}%` : '—'}
                    </b>
                  </div>
                );
              })
            )}
            <p style={{ fontSize: 11, color: 'var(--ink3)', margin: '6px 0 0' }}>
              Fonte: eleg.pesquisa · somente metodologias comparáveis
            </p>
          </div>
        </div>
      </div>

      {doMandato && <HistoricoSecao candidatoId={doMandato.id} />}
    </div>
  );
}
